import React, { useEffect, useState } from 'react';
import { createUseStyles } from 'src/theming';
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from '@material-ui/core';
import { Controller, useForm } from 'react-hook-form';
import { CreateContactOptions, LegacyContact } from 'src/interfaces/api/Contact';
import { validateEmail } from 'src/utils/StringUtils';

const useStyles = createUseStyles({
  root: {},
  title: {
    '& h2': {
      color: 'rgba(0, 0, 0, 0.87)',
    },
  },
  error: {
    color: '#f44336',
    fontSize: '.75rem',
    padding: '0 25px',
  },
  formControl: {
    marginBottom: '16px !important',
  },
});

interface Props {
  siteId: string;
  open: boolean;
  onClose: () => void;
  onValidate: (contactOptions: CreateContactOptions) => void;
  legacyContact: LegacyContact;
}

const MigrateContactDialog: React.FC<Props> = ({ open, onClose, onValidate, legacyContact }) => {
  const classes = useStyles();

  const [formError, setFormError] = useState<string | undefined>();

  useEffect(() => {
    setFormError(undefined);
  }, []);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<CreateContactOptions>({
    defaultValues: {
      lastname: legacyContact.lastname || '',
      firstname: legacyContact.name || '',
      email: legacyContact.email || '',
      phoneNumber: legacyContact.phoneNumber || '',
      companyName: legacyContact.companyName || '',
      origin: legacyContact.origin?.labelOrigin || '',
      job: legacyContact.job || '',
      legacyContactId: legacyContact.id,
      sendResetPasswordEmail: true,
    },
  });

  const onSubmit = async (contactOptions: CreateContactOptions) => {
    try {
      setFormError(undefined);
      await onValidate(contactOptions);
    } catch (e) {
      setFormError(e.message);
    }
  };

  return (
    <Dialog onClose={onClose} scroll='paper' open={open}>
      <DialogTitle className={classes.title}>Création d'un accès à l'espace client</DialogTitle>

      <DialogContent dividers>
        <Controller
          name='lastname'
          control={control}
          rules={{ required: { value: true, message: 'Le champ est requis' } }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              className={classes.formControl}
              label='Nom'
              fullWidth
              error={undefined !== error}
              helperText={error?.message}
            />
          )}
        />

        <Controller
          name='firstname'
          control={control}
          rules={{ required: { value: true, message: 'Le champ est requis' } }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              className={classes.formControl}
              label='Prénom'
              fullWidth
              error={undefined !== error}
              helperText={error?.message}
            />
          )}
        />

        <Controller
          name='email'
          control={control}
          rules={{
            required: { value: true, message: 'Le champ est requis' },
            validate: (email) => validateEmail(email) || 'Email invalide',
          }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              className={classes.formControl}
              label='Email'
              fullWidth
              error={undefined !== error}
              helperText={error?.message}
            />
          )}
        />

        <Controller
          name='phoneNumber'
          control={control}
          rules={{
            required: { value: true, message: 'Le champ est requis' },
            validate: (phone) => /^[0-9 .+]+$/.test(phone) || 'Numéro de téléphone invalide',
          }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              className={classes.formControl}
              label='Téléphone'
              fullWidth
              error={undefined !== error}
              helperText={error?.message}
            />
          )}
        />

        <Controller
          name='job'
          control={control}
          rules={{ required: { value: true, message: 'Le champ est requis' } }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              className={classes.formControl}
              label='Job'
              fullWidth
              error={undefined !== error}
              helperText={error?.message}
            />
          )}
        />

        <Controller
          name='sendResetPasswordEmail'
          control={control}
          render={({ field: { value, ...otherField } }) => (
            <FormControlLabel
              control={<Checkbox checked={value} {...otherField} />}
              label="Envoyer un mail d'initialisation de mot de passe"
            />
          )}
        />
      </DialogContent>

      <DialogActions>
        <Button type='button' onClick={onClose} color='primary'>
          Annuler
        </Button>

        <Button type='submit' color='primary' disabled={isSubmitting} onClick={handleSubmit(onSubmit)}>
          Migrer {isSubmitting && <CircularProgress size={15} />}
        </Button>
      </DialogActions>

      {formError && <p className={classes.error}>{formError}</p>}
    </Dialog>
  );
};

export default MigrateContactDialog;
